import { useState, useEffect } from 'react';
import { 
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Box,
  SvgIcon
} from '@mui/material';
import { EstadosService } from '../../../services/estados.service';
import { ComprasAgilesService } from '../../../services/compras-agiles.service';
import { useLoadingStore } from '../../../store/loadingStore';
import { useAlertStore } from '../../../store/alertStore';
import { useSelectionStore } from '../../../store/selectionStore';
import { useLicitacionesStore } from '../../../store/licitacionesStore';
import { useComprasAgilesStore } from '../../../store/comprasAgilesStore';
import { useEstados } from '../../../hooks/useEstados';
import { getStatusColor } from '../../../utils/statusUtils';

const StatusIcon = () => (
  <SvgIcon sx={{ 
    fontSize: 19,
    mr: 0,
    mt: '7px'
  }}>
    <path d="M14.63 0.84C14.27 0.33 13.67 0 13 0L2 0.0100002C0.9 0.0100002 0 0.9 0 2V12C0 13.1 0.9 13.99 2 13.99L13 14C13.67 14 14.27 13.67 14.63 13.16L19 7L14.63 0.84ZM13 12H2V2H13L16.55 7L13 12Z" fill="#D9D9D9"/>
  </SvgIcon>
);

interface StatusFilterProps {
  currentStatus?: string;
  licitacionId?: string;
  onStatusChange?: (newStatus: string) => void;
  entityType?: 'licitaciones' | 'compras-agiles';
}

export const StatusFilter: React.FC<StatusFilterProps> = ({ 
  currentStatus, 
  licitacionId, 
  onStatusChange,
  entityType = 'licitaciones'
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const setLoading = useLoadingStore((state) => state.setLoading);
  const showAlert = useAlertStore((state) => state.showAlert);
  const { selectedIds, clearSelection } = useSelectionStore();
  const fetchLicitaciones = useLicitacionesStore(state => state.fetchLicitaciones);
  const fetchComprasAgiles = useComprasAgilesStore(state => state.fetchComprasAgiles);
  const { estados, estadosInverso, isLoadedInternos } = useEstados();

  const isDetailMode = Boolean(currentStatus && licitacionId);

  const getNombreStatusUI = (estadoInterno: string): string => {
    switch (estadoInterno) {
      case 'NO_PROCESABLE':
        return 'PENDIENTE';
      case 'APROBADA':
        return 'SEGUIDA';
      case 'DESCARTADA':
        return 'DESCARTADA';
      default:
        return estadoInterno;
    }
  };

  const estadosPermitidos = Object.entries(estados).filter(([estado, codigo]) => 
    estado !== 'PROCESABLE'
  );

  useEffect(() => {
    if (isDetailMode && currentStatus && isLoadedInternos && Object.keys(estados).length > 0) {
      const numeroEstado = Number(currentStatus);
      if (!isNaN(numeroEstado)) {
        const estadoInterno = estadosInverso[numeroEstado];
        const estadoDisponible = estadosPermitidos.find(([_, codigo]) => codigo === numeroEstado);
        if (estadoInterno && estadoDisponible) {
          setSelectedStatus(numeroEstado.toString());
        } else {
          setSelectedStatus(''); // Valor vacío seguro
        }
      } else {
        setSelectedStatus(currentStatus);
      }
    }
  }, [currentStatus, isDetailMode, estadosInverso, estados, isLoadedInternos, estadosPermitidos]);

  const handleChange = async (event: SelectChangeEvent<string>) => {
    const estadoNumero = event.target.value;
    setSelectedStatus(estadoNumero);

    if (isDetailMode && licitacionId) {
      setLoading(true);
      try {
        if (entityType === 'compras-agiles') {
          await ComprasAgilesService.actualizarEstados([{
            codigo: licitacionId,
            nuevo_estado: Number(estadoNumero)
          }]);
          showAlert('Estado actualizado correctamente', 'success');
          await fetchComprasAgiles({ silent: true });
        } else {
          await EstadosService.actualizarEstados([{
            codigo: licitacionId,
            nuevo_estado: Number(estadoNumero)
          }]);
          showAlert('Estado actualizado correctamente', 'success');
          await fetchLicitaciones({ silent: true });
        }
        
        if (onStatusChange) {
          onStatusChange(estadoNumero);
        }
      } catch (error) {
        showAlert(error instanceof Error ? error.message : 'Error al actualizar estado', 'error');
      } finally {
        setLoading(false);
      }
    } else {
      if (selectedIds.length === 0) {
        const entityName = entityType === 'compras-agiles' ? 'compra ágil' : 'licitación';
        showAlert(`Selecciona al menos una ${entityName}`, 'warning');
        return;
      }

      setLoading(true);
      try {
        if (entityType === 'compras-agiles') {
          await ComprasAgilesService.actualizarEstados(
            selectedIds.map(id => ({
              codigo: id,
              nuevo_estado: Number(estadoNumero)
            }))
          );
          showAlert('Estados actualizados correctamente', 'success');
          clearSelection();
          await fetchComprasAgiles();
        } else {
          await EstadosService.actualizarEstados(
            selectedIds.map(id => ({
              codigo: id,
              nuevo_estado: Number(estadoNumero)
            }))
          );
          showAlert('Estados actualizados correctamente', 'success');
          clearSelection();
          await fetchLicitaciones();
        }
        setSelectedStatus('');
      } catch (error) {
        showAlert(error instanceof Error ? error.message : 'Error al actualizar estados', 'error');
        setSelectedStatus('');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <FormControl sx={{ width: '150px' }}>
        <Select
          labelId="status-filter-label"
          id="status-filter"
          value={selectedStatus}
          onChange={handleChange}
          displayEmpty
          disabled={!isDetailMode && selectedIds.length === 0}
          renderValue={(value) => {
            if (!value) return (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                height: '100%'
              }}>
                <StatusIcon />
                Cambiar Estado
              </Box>
            );
            
            const estadoInterno = estadosInverso[Number(value)];
            return (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                height: '100%',
                gap: 1
              }}>
                <StatusIcon />
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: getStatusColor(Number(value)),
                  }}
                />
                {estadoInterno ? getNombreStatusUI(estadoInterno) : value}
              </Box>
            );
          }}
          sx={{
            height: '30px',
            backgroundColor: '#FFFFFF',
            borderRadius: '5px',
            fontWeight: 400,
            fontSize: '10px',
            lineHeight: '100%',
            letterSpacing: '0%',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#E6E6E6',
              borderWidth: '1px'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#E6E6E6',
              borderWidth: '1px'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#E6E6E6',
              borderWidth: '1px'
            },
            '& .MuiSelect-select': {
              minHeight: 'unset !important',
              padding: '0 14px',
              display: 'flex',
              alignItems: 'center',
              height: '100%'
            }
          }}
        >
          {estadosPermitidos.map(([estado, codigo]) => (
            <MenuItem 
              key={codigo} 
              value={codigo.toString()}
              sx={{
                fontWeight: 400,
                fontSize: '10px',
                lineHeight: '100%',
                letterSpacing: '0%',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: getStatusColor(codigo),
                }}
              />
              {getNombreStatusUI(estado)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}; 